import React, {useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useProfile} from "./hooks/useProfile";
import {PERM_MASTER, PERM_SORTING, PERM_UNIT} from "shared/constants/consts.common";
import menuData from "./datas/menu.data.json";
import menuhubData from "./datas/menuhub.data.json";
import {Button} from "shared/ui";
import {cn} from "shared/lib/fns";
import packageJson from "../../package.json";

const app_version = packageJson.version;
const MAP_SEARCH_PARAM_KEYS = ["map_center_lat", "map_center_lng", "map_zoom"] as const;

type MenuItem = {
  key?: string;
  label: string;
  type?: "group" | string;
};

const isPrivilegedUser = (permissionGroup?: string[]) => {
  if (!permissionGroup) {
    return false;
  }
  
  return permissionGroup.includes(PERM_MASTER) || permissionGroup.includes(PERM_SORTING);
};

const isUnitUser = (permissionGroup?: string[]) => {
  return !!permissionGroup?.includes(PERM_UNIT);
};

const getMenuItems = (permissionGroup?: string[]): MenuItem[] => {
  if (isPrivilegedUser(permissionGroup)) {
    return menuData;
  }
  
  if (isUnitUser(permissionGroup)) {
    return [];
  }
  
  return menuhubData;
};

const isGroupMenuItem = (menuItem: MenuItem) => menuItem.type === "group";

const normalizePath = (path?: string) => {
  if (!path) {
    return "";
  }
  
  return path.endsWith("/") ? path.slice(0, -1) : path;
};

const getMapSearch = (search: string) => {
  const currentParams = new URLSearchParams(search);
  const nextParams = new URLSearchParams();

  MAP_SEARCH_PARAM_KEYS.forEach((key) => {
    const value = currentParams.get(key);

    if (value?.length) {
      nextParams.set(key, value);
    }
  });

  const nextSearch = nextParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

const SideMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {profile} = useProfile();
  const {permission_group, phonenumber, fullname} = profile;
  const currentPath = normalizePath(location.pathname);
  
  const menuItems = useMemo(() => getMenuItems(permission_group), [permission_group]);
  
  const handleSelect = (key?: string) => {
    const nextSearch = getMapSearch(location.search);

    if (key) {
      navigate({pathname: key, search: nextSearch});
      return;
    }
    navigate({pathname: "/home", search: nextSearch});
  };
  return (<aside className={"side-menu"}>
    <nav className={"nav"}>
      {menuItems.map((menuItem, index) => {
        if (isGroupMenuItem(menuItem)) {
          return (<span
            key={`${menuItem.label}-${index}`}
            className={"group"}
          >
              {menuItem.label}
            </span>);
        }
        
        const itemKey = menuItem.key;
        const isActive = normalizePath(itemKey) === currentPath;
        
        return (<Button
          key={itemKey || `${menuItem.label}-${index}`}
          variant="primary"
          onClick={() => handleSelect(itemKey)}
          className={cn("item", isActive && "is-active")}
          innerClassName={"inner"}
        >
          {menuItem.label}
        </Button>);
      })}
    </nav>
    <div className={"footer"}>
      <div className={"version"}>{"v" + app_version}</div>
      <div className={"profile"}>
        {fullname}
        <br />
        {phonenumber}
      </div>
    </div>
  </aside>);
};

export default SideMenu;
